// src/pages/agro/Reproduccion.js
import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'

const TIPOS = ['monta', 'inseminacion', 'parto', 'aborto', 'destete', 'preñez_confirmada']
const EMPTY = { madre_id: '', padre_id: '', tipo: 'monta', fecha: new Date().toISOString().slice(0,10), resultado: '', crias_nacidas: 0, notas: '' }

export default function Reproduccion() {
  const { profile } = useAuth()
  const canWrite = ['admin', 'capataz', 'veterinario'].includes(profile?.rol)
  const [eventos, setEventos] = useState([])
  const [animales, setAnimales] = useState([])
  const [hembras, setHembras] = useState([])
  const [machos, setMachos] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)

  useEffect(() => { loadData() }, [])

  async function loadData() {
    setLoading(true)
    const [eRes, aRes] = await Promise.all([
      supabase.from('eventos_reproduccion').select(`
        *, madre:animales!madre_id(codigo, nombre), padre:animales!padre_id(codigo, nombre)
      `).order('fecha', { ascending: false }).limit(100),
      supabase.from('animales').select('id, codigo, nombre, sexo').eq('estado', 'activo').order('codigo'),
    ])
    setEventos(eRes.data ?? [])
    const all = aRes.data ?? []
    setHembras(all.filter(a => a.sexo === 'hembra'))
    setMachos(all.filter(a => a.sexo === 'macho'))
    setLoading(false)
  }

  function onChange(e) { setForm(f => ({ ...f, [e.target.name]: e.target.value })) }

  async function save() {
    if (!form.tipo || !form.fecha) return alert('Tipo y fecha son obligatorios')
    setSaving(true)
    await supabase.from('eventos_reproduccion').insert([{ ...form, registrado_por: profile.id, crias_nacidas: parseInt(form.crias_nacidas) || 0 }])
    setSaving(false); setModal(false); setForm(EMPTY); loadData()
  }

  const TIPO_ICON = { monta: '🐂', inseminacion: '🔬', parto: '🐣', aborto: '❌', destete: '🍼', preñez_confirmada: '✅' }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">🐣 Reproducción</div>
          <div className="page-subtitle">Natalidad, partos y eventos reproductivos</div>
        </div>
        {canWrite && <button className="btn btn-primary" onClick={() => { setForm(EMPTY); setModal(true) }}>+ Registrar evento</button>}
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading
            ? <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner spinner-dark" style={{ margin: 'auto' }} /></div>
            : (
              <table>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Fecha</th>
                    <th>Madre</th>
                    <th>Padre</th>
                    <th>Crías</th>
                    <th>Resultado</th>
                    <th>Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {eventos.length === 0 && <tr><td colSpan={7}><div className="empty-state">Sin eventos registrados</div></td></tr>}
                  {eventos.map(e => (
                    <tr key={e.id}>
                      <td><span style={{ fontSize: 16 }}>{TIPO_ICON[e.tipo]}</span> {e.tipo}</td>
                      <td>{new Date(e.fecha).toLocaleDateString('es')}</td>
                      <td>{e.madre ? `${e.madre.codigo} ${e.madre.nombre ? `(${e.madre.nombre})` : ''}` : '—'}</td>
                      <td>{e.padre ? `${e.padre.codigo} ${e.padre.nombre ? `(${e.padre.nombre})` : ''}` : '—'}</td>
                      <td>{e.crias_nacidas > 0 ? <span className="badge badge-green">{e.crias_nacidas}</span> : '—'}</td>
                      <td>{e.resultado || '—'}</td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.notas || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">Nuevo evento reproductivo</div>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(false)}>✕</button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Tipo *</label>
                <select className="form-select" name="tipo" value={form.tipo} onChange={onChange}>
                  {TIPOS.map(t => <option key={t} value={t}>{TIPO_ICON[t]} {t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Fecha *</label>
                <input className="form-input" type="date" name="fecha" value={form.fecha} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Madre (hembra)</label>
                <select className="form-select" name="madre_id" value={form.madre_id} onChange={onChange}>
                  <option value="">Seleccionar...</option>
                  {hembras.map(a => <option key={a.id} value={a.id}>{a.codigo} {a.nombre ? `(${a.nombre})` : ''}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Padre (macho)</label>
                <select className="form-select" name="padre_id" value={form.padre_id} onChange={onChange}>
                  <option value="">Seleccionar...</option>
                  {machos.map(a => <option key={a.id} value={a.id}>{a.codigo} {a.nombre ? `(${a.nombre})` : ''}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Crías nacidas</label>
                <input className="form-input" type="number" min="0" name="crias_nacidas" value={form.crias_nacidas} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Resultado</label>
                <input className="form-input" name="resultado" value={form.resultado} onChange={onChange} placeholder="Exitoso, complicado..." />
              </div>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Notas</label>
                <textarea className="form-textarea" name="notas" value={form.notas} onChange={onChange} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
